/**
 * Makes clickable elements toggle the display of another set of elements.
 *
 * @param elementToToggle A CSS selector targeting the element(s) to toggle.
 * @param useHiddenAttribute (Optional) Whether to use the standard "hidden" attribute
 *     rather than <tt>hiddenClass</tt>
 * @param hiddenClass (Optional) A CSS class to apply to <tt>elementToToggle</tt>
 *     when it is hidden.
 * @param toggleEl (Optional) A CSS selector targeting an alternate element on which
 *     to apply shownClass/hiddenClass when the toggle state changes. Default: the
 *     element on which this module is called.
 * @apram toggleElShownClass (Optional) A CSS class to apply to <tt>toggleEl</tt>
 *     when <tt>elementToToggle</tt> is displayed.
 * @apram toggleElHiddenClass (Optional) A CSS class to apply to <tt>toggleEl</tt>
 *     when <tt>elementToToggle</tt> is hidden.
 * @param displayOnInit (Optional) A truthy value indicating whether elementToToggle
 *     should be displayed when this module initializes.
 *     May be a full object, in which case the following properties are supported:
 *         @property untilHiddenByUser If provided and truthy, elementToToggle won't
 *             be displayed anymore once the user has used the toggle, for the time of
 *             the browser session. <tt>cookieName</tt> MUST be set to use this feature.
 *         @property cookieName The name of the session cookie remembering
 *             whether the user has used the toggle. To be used with <tt>untilHiddenByUser</tt>
 *         @property ifTrue A CSS selector targeting a checkbox that should be checked, or an
 *             input which value should be true (not implemented yet). "this" may be used to
 *             target this component's $root.
 */
Hop.defineModule('display-toggle', (root, param) => {
    const $root = $(root);
    const $elementToToggle = $(param.elementToToggle);
    const $toggle = (param.toggleEl && $root.find(param.toggleEl)) || $root;

    function particularRule(rule) {
        if (rule === 'experienceRecommendation') {
            const url = new Url();
            const ruleApplication = url.hash.substring(0, 10) === 'experience' ? true : false;

            return ruleApplication;
        } else {
            return false;
        }
    }

    function updateToggleClass(display) {
        if (typeof display === 'boolean') {
            if (param.toggleElShownClass) {
                $toggle.toggleClass(param.toggleElShownClass, display);
            }
            if (param.toggleElHiddenClass) {
                $toggle.toggleClass(param.toggleElHiddenClass, !display);
            }
        } else {
            if (param.toggleElShownClass) {
                $toggle.toggleClass(param.toggleElShownClass);
            }
            if (param.toggleElHiddenClass) {
                $toggle.toggleClass(param.toggleElHiddenClass);
            }
        }

        if (param.useHiddenAttribute) {
            const elementToToggle = $elementToToggle[0];
            if (typeof display === 'boolean') {
                elementToToggle.hidden = !display;
            } else {
                elementToToggle.hidden = !elementToToggle.hidden;
            }
        } else if (param.hiddenClass && param.animatedToggle) {
            if (typeof display === 'boolean') {
                $elementToToggle.toggleClass(param.hiddenClass, !display);
            } else {
                $elementToToggle.toggleClass(param.hiddenClass);
            }
        }
    }

    function toggle(display) {
        if (!param.animatedToggle) {
            $elementToToggle.toggle(display);
        }
        updateToggleClass(display);
    }

    function displayOnInit() {
        if (particularRule(param.rule)) {
            return;
        }
        if (param.displayOnInit.untilHiddenByUser && Hop.getCookie(param.displayOnInit.cookieName) === 'ok') {
            return;
        }

        if (param.displayWithDelay) {
            setTimeout(() => {
                toggle(true);
            }, param.displayWithDelay);
        } else if (param.displayOnInit.ifTrue) {
            const $input = param.displayOnInit.ifTrue === 'this' ? $root : $(param.displayOnInit.ifTrue);
            if ($input.is('input[type="checkbox"]')) {
                toggle($input[0].checked);
            }
        } else {
            toggle(true);
        }
    }

    function maybeScrollToElement() {
        if (!param.scrollToElementAfterToggle) {
            return;
        }

        const $el = param.scrollToElementAfterToggle.parent ? $root.parents(param.scrollToElementAfterToggle.parent) : $root;

        $('html, body').animate(
            {
                scrollTop: $el.offset().top - $('.hopheader').height() + 'px',
            },
            'fast',
        );
    }

    $root.click((e) => {
        if (!$root.is('input[type="checkbox"], input[type="radio"]')) {
            e.preventDefault();
        }
        toggle();
        maybeScrollToElement();

        if (param.displayOnInit && param.displayOnInit.untilHiddenByUser) {
            Hop.setCookie(param.displayOnInit.cookieName, 'ok', {path: '/'});
        }
        if (param.rememberState && param.rememberState.cookieName) {
            Hop.setCookie(param.rememberState.cookieName, '' + $elementToToggle.is(':visible'), {path: '/'});
        }
    });

    if (param.rememberState && param.rememberState.cookieName) {
        toggle(Hop.getCookie(param.rememberState.cookieName) !== 'false');
    }

    if ($elementToToggle.is(':visible') && !param.animatedToggle) {
        updateToggleClass(true);
    }

    if (param.hideUponOutsideClick) {
        $(document).on('click', (e) => {
            if (
                (!e.target.closest(param.elementToToggle) && !param.toggleEl && e.target !== $toggle[0]) ||
                (param.toggleEl && !e.target.closest(param.toggleEl))
            ) {
                toggle(false);
            }
        });
    }

    if (param.displayOnInit) {
        displayOnInit();
    }
});
